/**
 * FormulaBuilder — visual builder for calculated-field formulas.
 *
 * Goals:
 *  - User picks referenced fields from a list (no typo'd field_keys).
 *  - Insert operators / parentheses / math functions with a click.
 *  - Live highlight of unknown / non-numeric tokens.
 *  - Live evaluation against author-supplied sample values so the user
 *    can sanity-check the formula without leaving the editor.
 *
 * Output: a plain formula string stored at `type_config.formula`. Format
 * matches `evaluateFormula` in `lib/form-engine/condition-engine.ts`:
 *   `={field_key} <op> {field_key} ...`
 *
 * The evaluator already handles the `=` prefix and `{field_key}` tokens,
 * so we don't need a separate AST — keeping the builder thin keeps the
 * runtime behavior identical to free-text editing.
 */
import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, Hash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils/cn'
import { evaluateFormula } from '@/lib/form-engine'
import type { FieldDefinition } from '../api/form-builder-api'

// Field types whose stored value is numeric-coercible (per evaluateFormula's
// `Number(val)` cast). Other types will be flagged in the formula.
const NUMERIC_TYPES = new Set(['number', 'calculated', 'boolean'])

interface FormulaBuilderProps {
  formula: string
  onChange: (next: string) => void
  /** All sibling fields in the same form, excluding the calculated field itself. */
  availableFields: FieldDefinition[]
}

const OPERATORS: Array<{ token: string; label: string }> = [
  { token: ' + ', label: '+' },
  { token: ' - ', label: '−' },
  { token: ' * ', label: '×' },
  { token: ' / ', label: '÷' },
  { token: ' ** ', label: 'xⁿ' },
  { token: ' % ', label: 'mod' },
  { token: '(',   label: '(' },
  { token: ')',   label: ')' },
]

const FUNCTIONS: Array<{ token: string; label: string; hint: string }> = [
  { token: 'Math.round()', label: 'گرد', hint: 'گرد کردن' },
  { token: 'Math.floor()', label: 'کف',  hint: 'گرد به پایین' },
  { token: 'Math.ceil()',  label: 'سقف', hint: 'گرد به بالا' },
  { token: 'Math.abs()',   label: '|x|', hint: 'قدر مطلق' },
  { token: 'Math.min(, )', label: 'min', hint: 'کمترین' },
  { token: 'Math.max(, )', label: 'max', hint: 'بیشترین' },
  { token: 'Math.sqrt()',  label: '√',   hint: 'جذر' },
]

function extractReferencedKeys(formula: string): string[] {
  const out: string[] = []
  const re = /\{([^}]+)\}/g
  let m: RegExpExecArray | null
  while ((m = re.exec(formula)) !== null) out.push(m[1])
  return out
}

export function FormulaBuilder({ formula, onChange, availableFields }: FormulaBuilderProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // Sample values for live preview, keyed by field_key.
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({})

  const fieldByKey = useMemo(() => {
    const m = new Map<string, FieldDefinition>()
    for (const f of availableFields) m.set(f.field_key, f)
    return m
  }, [availableFields])

  const referenced = useMemo(() => extractReferencedKeys(formula), [formula])
  const unknownRefs = useMemo(
    () => referenced.filter((k) => !fieldByKey.has(k)),
    [referenced, fieldByKey],
  )
  const nonNumericRefs = useMemo(
    () =>
      referenced.filter((k) => {
        const f = fieldByKey.get(k)
        return f ? !NUMERIC_TYPES.has(f.field_type) : false
      }),
    [referenced, fieldByKey],
  )

  function insertAtCursor(token: string) {
    const el = textareaRef.current
    const current = formula ?? ''
    let next: string
    let nextCursor: number
    if (!el) {
      next = current + token
      nextCursor = next.length
    } else {
      const start = el.selectionStart ?? current.length
      const end = el.selectionEnd ?? current.length
      next = current.slice(0, start) + token + current.slice(end)
      nextCursor = start + token.length
    }
    if (!next.startsWith('=')) next = '=' + next
    onChange(next)
    // Restore cursor after React re-render.
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const offset = next.startsWith('=') && !current.startsWith('=') ? 1 : 0
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(nextCursor + offset, nextCursor + offset)
      }
    })
  }

  // Live preview value using author-supplied sample inputs.
  const previewResult = useMemo(() => {
    if (!formula.trim()) return null
    const ctx: Record<string, unknown> = {}
    for (const k of referenced) {
      const raw = sampleValues[k]
      if (raw === undefined || raw === '') continue
      const n = Number(raw)
      ctx[k] = isNaN(n) ? raw : n
    }
    return evaluateFormula(formula, ctx)
  }, [formula, referenced, sampleValues])

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">سازنده فرمول</Label>
        {referenced.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {referenced.length} ارجاع
          </span>
        )}
      </div>

      {/* ── Field picker ─────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label className="text-[10px] text-muted-foreground">فیلدهای قابل استفاده</Label>
        {availableFields.length === 0 ? (
          <p className="text-[10px] text-muted-foreground italic">
            هیچ فیلدی برای ارجاع تعریف نشده است
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {availableFields.map((f) => {
              const isNumeric = NUMERIC_TYPES.has(f.field_type)
              const isUsed = referenced.includes(f.field_key)
              return (
                <button
                  key={f.field_id}
                  type="button"
                  onClick={() => insertAtCursor(`{${f.field_key}}`)}
                  title={isNumeric ? '' : 'این فیلد عددی نیست'}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-mono transition-colors',
                    isUsed
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background hover:border-primary/50',
                    !isNumeric && 'opacity-60',
                  )}
                >
                  <Hash className="w-2.5 h-2.5" />
                  {f.field_key}
                  {!isNumeric && (
                    <span className="text-[9px] text-muted-foreground">
                      ({f.field_type})
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Operators ────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label className="text-[10px] text-muted-foreground">عملگرها</Label>
        <div className="flex flex-wrap gap-1">
          {OPERATORS.map((op) => (
            <Button
              key={op.token}
              type="button"
              variant="outline"
              size="sm"
              className="h-6 w-9 p-0 text-xs font-mono"
              onClick={() => insertAtCursor(op.token)}
            >
              {op.label}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Functions ────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label className="text-[10px] text-muted-foreground">توابع</Label>
        <div className="flex flex-wrap gap-1">
          {FUNCTIONS.map((fn) => (
            <Button
              key={fn.token}
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px] font-mono"
              title={fn.hint}
              onClick={() => insertAtCursor(fn.token)}
            >
              {fn.label}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Editor ───────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label className="text-[10px] text-muted-foreground">فرمول</Label>
        <Textarea
          ref={textareaRef}
          value={formula}
          onChange={(e) => {
            const v = e.target.value
            onChange(v && !v.startsWith('=') ? '=' + v : v)
          }}
          placeholder="={weight_kg} / ({height_cm} / 100) ** 2"
          rows={3}
          className="text-xs font-mono"
          dir="ltr"
        />
        {formula && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-destructive"
              onClick={() => onChange('')}
            >
              پاک کردن
            </Button>
          </div>
        )}
      </div>

      {/* ── Validation messages ──────────────────────────────────────── */}
      {(unknownRefs.length > 0 || nonNumericRefs.length > 0) && (
        <div className="space-y-1">
          {unknownRefs.length > 0 && (
            <div className="flex items-start gap-1.5 text-[10px] text-destructive">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                فیلد ناشناخته:{' '}
                {unknownRefs.map((k) => (
                  <code key={k} className="font-mono mx-0.5">{`{${k}}`}</code>
                ))}
              </span>
            </div>
          )}
          {nonNumericRefs.length > 0 && (
            <div className="flex items-start gap-1.5 text-[10px] text-amber-600 dark:text-amber-500">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                فیلد غیرعددی:{' '}
                {nonNumericRefs.map((k) => (
                  <code key={k} className="font-mono mx-0.5">{`{${k}}`}</code>
                ))}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Live preview ─────────────────────────────────────────────── */}
      {referenced.length > 0 && unknownRefs.length === 0 && (
        <div className="space-y-1.5 rounded-md border border-border bg-background p-2">
          <Label className="text-[10px] text-muted-foreground">پیش‌نمایش زنده</Label>
          <div className="grid grid-cols-2 gap-1.5">
            {referenced.map((k) => {
              const f = fieldByKey.get(k)
              return (
                <div key={k} className="space-y-0.5">
                  <Label className="text-[9px] font-mono text-muted-foreground" dir="ltr">
                    {k}
                  </Label>
                  <Input
                    value={sampleValues[k] ?? ''}
                    onChange={(e) =>
                      setSampleValues((prev) => ({ ...prev, [k]: e.target.value }))
                    }
                    placeholder={f ? f.label.fa : ''}
                    className="h-6 text-[10px] font-mono"
                    dir="ltr"
                  />
                </div>
              )
            })}
          </div>
          <div className="flex items-center gap-2 pt-1 border-t border-border">
            <span className="text-[10px] text-muted-foreground">نتیجه:</span>
            {previewResult === null ? (
              <Badge variant="outline" className="text-[10px] font-mono">—</Badge>
            ) : (
              <Badge className="text-[10px] font-mono">{previewResult}</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

